import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CMP-06 and VEN-05 at v1.966, compliance part 2: kinds of cover, what each venue asks to
// see, the company's certificates of currency, and the jobs whose venue's cover is not met.
// Flagged, never refused.

export type InsuranceKind = components['schemas']['TicketTypeView'];
export type RequiredCover = components['schemas']['RequiredCoverView'];
export type Certificate = components['schemas']['CertificateView'];
export type InsuranceGap = components['schemas']['InsuranceGapView'];
export type JobInsuranceGaps = components['schemas']['JobInsuranceGapsView'];
export type CoverReason = InsuranceGap['reason'];
type Money = components['schemas']['MoneyView'];

export const REASON: Record<CoverReason, string> = {
  none: 'none held',
  below_minimum: 'held, but below the minimum',
  lapses: 'lapses before the job ends',
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const insuranceKindsQuery = queryOptions({
  queryKey: ['insurance-kinds'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/insurance-kinds');
    if (error) throw problem(error, 'Could not load the kinds of cover.');
    return data;
  },
});

export async function addInsuranceKind(name: string) {
  const { data, error } = await api.POST('/v1/insurance-kinds', { body: { name } });
  if (error) throw problem(error, 'Could not add that kind of cover.');
  return data;
}

export const requiredCoversQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['required-covers', venueId],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/required-covers', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the insurance this venue asks for.');
      return data;
    },
  });

export async function setRequiredCovers(
  venueId: string,
  covers: Array<{ insuranceKindId: string; minimum: Money | null }>,
) {
  const { data, error } = await api.PUT('/v1/venues/{id}/required-covers', {
    params: { path: { id: venueId } },
    body: { covers },
  });
  if (error) throw problem(error, 'Could not save the insurance this venue asks for.');
  return data;
}

export const certificatesQuery = queryOptions({
  queryKey: ['insurance-certificates'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/insurance-certificates');
    if (error) throw problem(error, 'Could not load the certificates.');
    return data;
  },
});

export interface CertificateInput {
  insuranceKindId: string;
  tradingEntityId: string;
  insurer: string;
  policyNumber: string | null;
  startsOn: string;
  expiresOn: string;
  cover: Money;
  fileId: string | null;
}

export async function addCertificate(body: CertificateInput) {
  const { data, error } = await api.POST('/v1/insurance-certificates', { body });
  if (error) throw problem(error, 'Could not record that certificate.');
  return data;
}

export async function retireCertificate(id: string) {
  const { error } = await api.POST('/v1/insurance-certificates/{id}/retire', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not remove that certificate.');
}

/** The certificate's PDF, kept on the trading entity it insures; returns the file's id. */
export async function uploadCertificatePdf(entityId: string, file: File): Promise<string> {
  const data = new FormData();
  data.append('file', file);
  const { data: kept, error } = await api.POST('/v1/document-settings/{entityId}/insurance-files', {
    params: { path: { entityId } },
    body: data as never,
    bodySerializer: (body) => body as unknown as FormData,
  });
  if (error) throw problem(error, 'Could not upload that PDF.');
  return kept.id;
}

export const jobInsuranceGapsQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['job-insurance-gaps', jobId],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/insurance-gaps', {
        params: { path: { id: jobId } },
      });
      if (error) throw problem(error, 'Could not check this job’s insurance.');
      return data;
    },
  });

export const upcomingInsuranceGapsQuery = queryOptions({
  queryKey: ['job-insurance-gaps', 'upcoming'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/job-insurance-gaps');
    if (error) throw problem(error, 'Could not check upcoming jobs’ insurance.');
    return data;
  },
});
